"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useState } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

interface BenefitSliderProps {
  data?: any;
}

const BenefitSlider: React.FC<BenefitSliderProps> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";
  const handleSwiper = (flag: boolean) => {
    if (!!flag)
      setCurrentIndex(currentIndex > 1 ? currentIndex - 1 : currentIndex);
    else
      setCurrentIndex(
        currentIndex < data?.card?.length ? currentIndex + 1 : currentIndex,
      );
  };
  return (
    <div className="relative w-full">
      {/* Swiper */}
      <Swiper
        modules={[Navigation, Autoplay]}
        navigation={{
          prevEl: ".prevBtn",
          nextEl: ".nextBtn",
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        loop={true}
        onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex + 1)}
      >
        {data?.card?.map((card: any, index: number) => (
          <SwiperSlide key={index}>
            <div>
              <Image
                src={
                  card?.image?.data?.attributes?.url
                    ? toWebp(`${baseUrl}${card.image.data.attributes.url}`)
                    : "/images/career-slide-img.png" // fallback image
                }
                alt={
                  card?.image?.data?.attributes?.alternativeText ||
                  card?.title ||
                  "Slide Image"
                }
                width={705}
                height={528}
                className="w-full h-[286px] md:h-[420px] xl:h-[528px] object-cover mb-[32px]"
              />
              <h5 className="text-[#040405] xl:text-[32px] xl:leading-[41.6px] md:text-[24px] md:leading-[31.2.6px] text-[22px] leading-[28.6px] font-[700] mb-[16px] md:text-left text-center">
                {card?.title}
              </h5>
              <div
                title={card?.text?.replace(/<[^>]+>/g, "") ?? ""}
                className="text-[#32343B] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] font-[600] mb-[16px] line-clamp-3 md:text-left text-center"
                dangerouslySetInnerHTML={{ __html: card?.text ?? "" }}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {/* Desktop Controls */}
      <div className="justify-center md:justify-start items-center gap-3 mt-[40px] hidden md:flex">
        <button
          onClick={() => handleSwiper(false)}
          aria-label="Previous"
          className="prevBtn p-2 border hover:border-[#0086E6] border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
        >
          <svg
            className="arrow arrow--left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M9.57 5.93L3.5 12l6.07 6.07"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-text-button"
            />
            <path
              d="M20.5 12H3.67"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-text-button"
            />
          </svg>
        </button>

        <div className="pagination-indicator text-sm font-medium">
          <span className="text-subtitle-1 font-bold text-secondary-text">
            {currentIndex ?? 1}/{data?.card?.length}
          </span>
        </div>

        <button
          onClick={() => handleSwiper(true)}
          aria-label="Next"
          className="nextBtn hover:border-[#0086E6] p-2 border border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
        >
          <svg
            className="arrow arrow--right"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M14.43 5.93L20.5 12l-6.07 6.07"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-text-button"
            />
            <path
              d="M3.5 12h16.83"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-text-button"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Progress Bar */}
      <div className="mt-[30px] lg:mt-[15px] md:hidden flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative ">
        {data?.card?.map((_: any, index: number) => (
          <div
            key={index}
            className={`p-0 relative rounded overflow-hidden ${
              currentIndex - 1 === index
                ? "flex w-[128px] h-1 bg-[#E6F3FD]"
                : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
            }`}
          >
            <span
              className={`absolute top-0 left-0 rounded h-1 ${
                currentIndex - 1 === index
                  ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[5000ms] w-[128px]"
                  : "w-[16px]"
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
export default BenefitSlider;
